import { FRIEND_DAILY_WIN_COPY, GAME_NAME, TURN_TIMEOUT_INIT_MS, TYPE } from "@gaoya/shared";
import {
  art,
  C,
  capsuleBox,
  clipName,
  drawArt,
  drawCircleAvatar,
  drawGearIcon,
  drawPanel,
  fillRound,
  roundPath,
  sectionTitle,
} from "../theme.js";
import { hallHomeLayout, hallRankRows, leftOfCapsule, settingsGearRect } from "../../ui/hall.js";
import { HONOR_WINS_COL, drawNameWithHonor, drawWinsCol, honorPreviewActive, honorPreviewRows } from "../widgets/honor.js";
import type { UiState } from "../../game/state.js";

type R = { x: number; y: number; w: number; h: number };

export function drawHallHeader(ctx: CanvasRenderingContext2D, ui: UiState, w: number, subtitle: string): void {
  const cap = capsuleBox(ui, w);
  const av = 32;
  const avPos = leftOfCapsule(ui, w, av, av);
  drawCircleAvatar(ctx, ui.avatarImg, avPos.x + av / 2, avPos.y + av / 2, av / 2 - 1, ui.nickname);
  const gear = settingsGearRect(ui, w);
  fillRound(ctx, gear.x, gear.y, gear.w, gear.h, gear.h / 2, C.card);
  ctx.strokeStyle = C.line;
  ctx.lineWidth = 1;
  roundPath(ctx, gear.x + 0.5, gear.y + 0.5, gear.w - 1, gear.h - 1, gear.h / 2);
  ctx.stroke();
  drawGearIcon(ctx, gear.x + gear.w / 2, gear.y + gear.h / 2, 9, C.ink2);

  const logo = art("icon");
  const ls = 36;
  const ly = cap.cy - ls / 2;
  if (logo) {
    ctx.save();
    roundPath(ctx, 16, ly, ls, ls, 9);
    ctx.clip();
    ctx.drawImage(logo, 16, ly, ls, ls);
    ctx.restore();
  }
  const tx = logo ? 16 + ls + 10 : 16;
  const maxW = Math.max(60, gear.x - tx - 8);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.ink;
  ctx.font = `700 18px ${TYPE.serif}`;
  ctx.fillText(clipName(ctx, GAME_NAME, maxW), tx, cap.cy - 8);
  ctx.fillStyle = C.ink2;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText(clipName(ctx, subtitle, maxW), tx, cap.cy + 11);
}

export function drawHall(ctx: CanvasRenderingContext2D, ui: UiState, w: number, h: number): void {
  drawHallHeader(ctx, ui, w, `欢迎回来，${ui.nickname}`);
  const g = hallHomeLayout(w, h, ui);

  if (ui.hallMenu === "rush-size") {
    sectionTitle(ctx, "选择抢答人数", g.title.x, g.title.y + 14, "人满后请准备", g.title.x + g.title.w);
    return;
  }

  sectionTitle(ctx, "选择对决", g.title.x, g.title.y + 14, `首尾相接 · ${Math.round(TURN_TIMEOUT_INIT_MS / 1000)} 秒作答`, g.title.x + g.title.w);
  drawMatchHero(ctx, g.match, ui.pointerDown && hit(g.match, ui));
  drawRushCard(ctx, g.rush3, 3, "抢答 3 人", "先手抢答 · 智破群儒", false, ui.pointerDown && hit(g.rush3, ui));
  drawRushCard(ctx, g.rush4, 4, "抢答 4 人", "四方角逐 · 唯快不破", true, ui.pointerDown && hit(g.rush4, ui));
  drawRoomCard(ctx, g.room);
  drawRankStrip(ctx, ui, g.rank);
}

function hit(r: R, ui: UiState): boolean {
  return ui.pointerX >= r.x && ui.pointerX <= r.x + r.w && ui.pointerY >= r.y && ui.pointerY <= r.y + r.h;
}

function drawMatchHero(ctx: CanvasRenderingContext2D, r: R, pressed: boolean): void {
  const dy = pressed ? 2 : 0;
  fillRound(ctx, r.x, r.y + 3, r.w, r.h, 18, C.sealDeep);
  const y = r.y + dy;
  ctx.save();
  roundPath(ctx, r.x, y, r.w, r.h, 18);
  ctx.clip();
  const g = ctx.createLinearGradient(r.x, y, r.x + r.w, y + r.h);
  g.addColorStop(0, "#C94A2F");
  g.addColorStop(1, "#A3301C");
  ctx.fillStyle = g;
  ctx.fillRect(r.x, y, r.w, r.h);
  ctx.fillStyle = "rgba(255,226,180,0.10)";
  ctx.beginPath();
  ctx.arc(r.x + r.w - 70, y + r.h * 0.45, r.h * 0.62, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(255,226,180,0.08)";
  ctx.beginPath();
  ctx.arc(r.x + r.w - 70, y + r.h * 0.45, r.h * 0.42, 0, Math.PI * 2);
  ctx.fill();
  const charH = r.h * 1.08;
  drawArt(ctx, "char-xia-cut", r.x + r.w - charH * 0.95, y + r.h - charH + 8, charH, charH);
  ctx.restore();
  ctx.strokeStyle = "rgba(255,240,220,0.28)";
  ctx.lineWidth = 1;
  roundPath(ctx, r.x + 5.5, y + 5.5, r.w - 11, r.h - 11, 14);
  ctx.stroke();

  const x = r.x + 20;
  ctx.textBaseline = "middle";
  const tagW = 64;
  fillRound(ctx, x, y + 18, tagW, 20, 10, "rgba(255,240,220,0.18)");
  ctx.fillStyle = "#FFE7C2";
  ctx.font = `700 11px ${TYPE.sans}`;
  ctx.textAlign = "center";
  ctx.fillText("人气之选", x + tagW / 2, y + 28.5);
  ctx.textAlign = "left";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `700 24px ${TYPE.serif}`;
  ctx.fillText("经典 1 对 1", x, y + 56);
  ctx.fillStyle = "rgba(255,255,255,0.82)";
  ctx.font = `13px ${TYPE.sans}`;
  ctx.fillText("轮流接龙 · 三盏茶命", x, y + 82);
  const by = y + r.h - 40;
  if (by > y + 92) {
    const bw = 104;
    fillRound(ctx, x, by + 2, bw, 28, 14, "rgba(90,20,10,0.35)");
    fillRound(ctx, x, by, bw, 28, 14, "#FFF7EE");
    ctx.fillStyle = C.seal;
    ctx.font = `700 13px ${TYPE.sans}`;
    ctx.textAlign = "center";
    ctx.fillText("开始匹配  ›", x + bw / 2, by + 14.5);
  } else {
    ctx.fillStyle = "#FFE7C2";
    ctx.font = `700 13px ${TYPE.sans}`;
    ctx.fillText("开始匹配  ›", x, y + 104);
  }
}

function drawPeopleDots(ctx: CanvasRenderingContext2D, rightX: number, cy: number, n: number, color: string): void {
  const s = 7;
  const gap = 4;
  for (let i = 0; i < n; i++) {
    const x = rightX - (n - i) * (s * 2 + gap) + gap + s;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, cy - 3, s * 0.45, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, cy + s + 1, s * 0.85, Math.PI, 0);
    ctx.fill();
  }
}

function drawRushCard(
  ctx: CanvasRenderingContext2D,
  r: R,
  n: number,
  titleText: string,
  desc: string,
  alt: boolean,
  pressed: boolean,
): void {
  const y = r.y + (pressed ? 2 : 0);
  drawPanel(ctx, r.x, y, r.w, r.h, 16, C.card);
  const badge = r.h < 104 ? 32 : 40;
  fillRound(ctx, r.x + 14, y + 14, badge, badge, 12, alt ? C.sealSoft : C.goldSoft);
  ctx.fillStyle = alt ? C.seal : C.goldDeep;
  ctx.font = `700 ${badge < 40 ? 20 : 24}px ${TYPE.serif}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(n), r.x + 14 + badge / 2, y + 14 + badge / 2 + 1);
  drawPeopleDots(ctx, r.x + r.w - 14, y + 14 + badge / 2 - 2, n, alt ? "rgba(184,58,36,0.45)" : "rgba(138,98,32,0.45)");
  ctx.textAlign = "left";
  ctx.fillStyle = C.ink;
  ctx.font = `700 17px ${TYPE.serif}`;
  ctx.fillText(titleText, r.x + 14, y + r.h - 36);
  ctx.fillStyle = C.ink2;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText(clipName(ctx, desc, r.w - 28), r.x + 14, y + r.h - 15);
}

function drawRoomCard(ctx: CanvasRenderingContext2D, r: R): void {
  drawPanel(ctx, r.x, r.y, r.w, r.h, 16, C.card);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.ink;
  ctx.font = `700 17px ${TYPE.serif}`;
  ctx.fillText("好友雅间", r.x + 16, r.y + 24);
  ctx.fillStyle = C.seal;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText(clipName(ctx, FRIEND_DAILY_WIN_COPY, r.w - 32), r.x + 16, r.y + 44);
}

const MEDAL: [string, string][] = [
  ["#E4B95F", "#6B4A12"],
  ["#D9D4CB", "#4E4740"],
  ["#E2B48C", "#6A3E1E"],
];

function drawRankStrip(ctx: CanvasRenderingContext2D, ui: UiState, r: R): void {
  drawPanel(ctx, r.x, r.y, r.w, r.h, 16, C.card);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.ink;
  ctx.font = `700 16px ${TYPE.serif}`;
  ctx.fillText("好友对战榜", r.x + 16, r.y + 24);
  ctx.fillStyle = C.seal;
  ctx.font = `600 12px ${TYPE.sans}`;
  ctx.textAlign = "right";
  ctx.fillText("查看总榜  ›", r.x + r.w - 16, r.y + 24);

  if (ui.hallRankCanvas) {
    // 微信：好友名次由开放数据域绘制（只有那里能读好友数据），这里贴图即可
    const rr = hallRankRows(r);
    ctx.drawImage(ui.hallRankCanvas, rr.x, rr.y, rr.w, rr.h);
    return;
  }
  const fromServer = ui.leaderboard.length > 0;
  const rows = honorPreviewActive()
    ? honorPreviewRows()
    : fromServer
      ? ui.leaderboard
      : [{ nickname: ui.nickname || "茶客", wins: ui.totalWins }];
  const rowH = 40;
  const top = r.y + 46;
  const room = Math.max(1, Math.floor((r.y + r.h - 10 - top) / rowH));
  const show = rows.slice(0, Math.min(room, 6));
  for (let i = 0; i < show.length; i++) {
    const row = show[i];
    if (!row) continue;
    const ry = top + i * rowH;
    const mine = row.nickname === ui.nickname;
    if (mine) fillRound(ctx, r.x + 8, ry + 2, r.w - 16, rowH - 4, 10, "rgba(228,185,95,0.18)");
    else if (i > 0) {
      ctx.fillStyle = C.line;
      ctx.fillRect(r.x + 16, ry, r.w - 32, 1);
    }
    const medal = MEDAL[i];
    const cy = ry + rowH / 2;
    if (medal) {
      fillRound(ctx, r.x + 18, cy - 11, 22, 22, 11, medal[0]);
      ctx.fillStyle = medal[1];
    } else {
      ctx.fillStyle = C.ink3;
    }
    ctx.font = `700 12px ${TYPE.sans}`;
    ctx.textAlign = "center";
    ctx.fillText(String(i + 1), r.x + 29, cy + 0.5);
    drawWinsCol(ctx, row.wins, r.x + r.w - 16, cy, 15);
    ctx.textAlign = "left";
    ctx.fillStyle = C.ink;
    ctx.font = `${mine ? 700 : 500} 14px ${TYPE.sans}`;
    drawNameWithHonor(ctx, row.nickname + (mine ? "（我）" : ""), row.wins, r.x + 52, cy, r.x + r.w - 16 - HONOR_WINS_COL);
  }
  const used = top + show.length * rowH;
  if (!fromServer && r.y + r.h - used > 36) {
    ctx.fillStyle = C.ink3;
    ctx.font = `12px ${TYPE.sans}`;
    ctx.textAlign = "center";
    ctx.fillText("与好友对战，胜场会计入此榜", r.x + r.w / 2, used + 22);
  }
}
